/**
 * metaColumnCollapse.ts
 * Persistence helpers for the Skill Matrix meta-column (Job Class / Work Group /
 * Tier) collapse state.
 *
 * Collapsed meta columns are stored in localStorage as a JSON array of
 * MetaColumn identifiers. The key is kept here so every read/write uses the
 * same slot.
 */

export const META_COLUMN_COLLAPSE_LS_KEY = 'skillMatrix.collapsedMetaColumns';

/** The meta columns that can be independently collapsed. */
export type MetaColumn = 'jobClass' | 'workGroup' | 'tier';

const VALID_META_COLUMNS: ReadonlySet<MetaColumn> = new Set<MetaColumn>([
  'jobClass',
  'workGroup',
  'tier',
]);

function isMetaColumn(x: unknown): x is MetaColumn {
  return typeof x === 'string' && VALID_META_COLUMNS.has(x as MetaColumn);
}

/**
 * Read collapsed meta column identifiers from localStorage.
 * Returns an empty array if localStorage is unavailable or the stored value
 * is malformed/contains invalid identifiers.
 */
export function readCollapsedMetaColumns(): MetaColumn[] {
  try {
    const raw = window.localStorage.getItem(META_COLUMN_COLLAPSE_LS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isMetaColumn);
  } catch {
    // localStorage blocked (private mode) or JSON parse failure — fall back.
  }
  return [];
}

/**
 * Persist the current collapsed meta column set to localStorage (best-effort).
 * If localStorage is unavailable the call is silently ignored.
 */
export function writeCollapsedMetaColumns(collapsed: Set<MetaColumn>): void {
  try {
    window.localStorage.setItem(
      META_COLUMN_COLLAPSE_LS_KEY,
      JSON.stringify(Array.from(collapsed)),
    );
  } catch {
    // Ignore write errors.
  }
}
